import { useEffect, useState } from "react";
import axios from "axios";
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';

interface AffiliateStats {
    _id: string;
    fullName: string;
    email: string;
    affiliateCode: string;
    approvedAt: string;
    stats: {
        totalOrders: number;
        totalSales: number;
        totalCommission: number;
        pendingCommission: number;
        paidCommission: number;
    };
}

interface AffiliateOrderItem {
    _id: string;
    affiliateId: any;
    orderId: any;
    affiliateCode: string;
    orderNumber: string;
    orderTotal: number;
    commission: number;
    commissionRate: number;
    status: string;
    createdAt: string;
    paidAt?: string;
}

export default function AffiliateOrders() {
    const { t } = useTranslation();
    const cookie = getCookies();

    const [loading, setLoading] = useState(true);
    const [affiliates, setAffiliates] = useState<AffiliateStats[]>([]);
    const [selectedAffiliate, setSelectedAffiliate] = useState<string | null>(null);
    const [orders, setOrders] = useState<AffiliateOrderItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [summary, setSummary] = useState({
        totalOrders: 0,
        totalSales: 0,
        totalCommission: 0,
        pendingCommission: 0,
        paidCommission: 0
    });
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

    const breadcrump = [
        { name: t('Dashboard'), url: '/dashboard' },
        { name: t('Affiliate'), url: '/dashboard/Affiliate/Requests' },
        { name: t('Affiliate Orders'), url: null },
    ];

    const loadAffiliates = async () => {
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/affiliate/stats/all`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            if (res.data.success) {
                setAffiliates(res.data.data);
            }
        } catch (error: any) {
            console.error('Error loading affiliates:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAffiliateOrders = async (affiliateId: string, page = 1) => {
        setOrdersLoading(true);
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/affiliate/${affiliateId}/orders`,
                params: { page, limit: 10 },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            if (res.data.success) {
                setOrders(res.data.message.docs);
                setSummary(res.data.summary);
                setPagination({
                    page: res.data.message.page,
                    pages: res.data.message.totalPages,
                    total: res.data.message.totalDocs
                });
            }
        } catch (error: any) {
            console.error('Error loading affiliate orders:', error);
        } finally {
            setOrdersLoading(false);
        }
    };

    const updateOrderStatus = async (orderId: string, newStatus: string) => {
        try {
            const result = await Swal.fire({
                title: t('Confirm Status Change'),
                text: t('Are you sure you want to change the status to') + ' ' + t(newStatus) + '?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('Yes'),
                cancelButtonText: t('Cancel')
            });

            if (!result.isConfirmed) return;

            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/affiliate/orders/${orderId}/status`,
                data: { status: newStatus },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Status updated successfully'),
                timer: 2000,
                showConfirmButton: false
            });

            if (selectedAffiliate) {
                loadAffiliateOrders(selectedAffiliate, pagination.page);
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Failed to update status')
            });
        }
    };

    useEffect(() => {
        loadAffiliates();
    }, []);

    useEffect(() => {
        if (selectedAffiliate) {
            loadAffiliateOrders(selectedAffiliate);
        }
    }, [selectedAffiliate]);

    const getStatusBadge = (status: string) => {
        const statusStyles: { [key: string]: string } = {
            pending: 'bg-yellow-100 text-yellow-800',
            confirmed: 'bg-blue-100 text-blue-800',
            paid: 'bg-green-100 text-green-800',
            canceled: 'bg-red-100 text-red-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
                {t(status.charAt(0).toUpperCase() + status.slice(1))}
            </span>
        );
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-EU', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump} />
                <HeaderOfPage name={t('Affiliate Orders')} />
                <div className="mt-6 bg-white rounded-xl shadow p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t('Loading...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={t('Affiliate Orders')} />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Affiliates List */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm">
                        <div className="p-4 border-b">
                            <h3 className="font-semibold text-gray-800">{t('Affiliates')}</h3>
                            <p className="text-sm text-gray-500">{affiliates.length} {t('approved affiliates')}</p>
                        </div>
                        <div className="max-h-[600px] overflow-y-auto">
                            {affiliates.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    {t('No approved affiliates found')}
                                </div>
                            ) : (
                                affiliates.map((affiliate) => (
                                    <div
                                        key={affiliate._id}
                                        onClick={() => setSelectedAffiliate(affiliate._id)}
                                        className={`p-4 border-b cursor-pointer transition-colors ${selectedAffiliate === affiliate._id
                                                ? 'bg-blue-50 border-l-4 border-l-blue-500'
                                                : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-medium text-gray-800">{affiliate.fullName}</p>
                                                <p className="text-sm text-gray-500">{affiliate.affiliateCode}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-green-600">
                                                    {formatCurrency(affiliate.stats.totalCommission)}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {affiliate.stats.totalOrders} {t('orders')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Orders Detail */}
                <div className="lg:col-span-2">
                    {!selectedAffiliate ? (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <p className="mt-4 text-gray-500">{t('Select an affiliate to view orders')}</p>
                        </div>
                    ) : ordersLoading ? (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-500">{t('Loading orders...')}</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-sm text-gray-500">{t('Total Orders')}</p>
                                    <p className="text-2xl font-bold text-gray-800">{summary.totalOrders}</p>
                                </div>
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-sm text-gray-500">{t('Total Sales')}</p>
                                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.totalSales)}</p>
                                </div>
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-sm text-gray-500">{t('Pending Commission')}</p>
                                    <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.pendingCommission)}</p>
                                </div>
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-sm text-gray-500">{t('Paid Commission')}</p>
                                    <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.paidCommission)}</p>
                                </div>
                            </div>

                            {/* Orders Table */}
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div className="p-4 border-b">
                                    <h3 className="font-semibold text-gray-800">{t('Orders')}</h3>
                                </div>
                                {orders.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500">
                                        {t('No orders found for this affiliate')}
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Order')}</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Date')}</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('Order Total')}</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('Commission')}</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('Status')}</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('Actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {orders.map((order) => (
                                                    <tr key={order._id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium text-gray-800">{order.orderNumber}</p>
                                                            <p className="text-xs text-gray-500">{order.commissionRate}% {t('rate')}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(order.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                                                            {formatCurrency(order.orderTotal)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-bold text-green-600">
                                                            {formatCurrency(order.commission)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {getStatusBadge(order.status)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <select
                                                                value={order.status}
                                                                onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                                                                className="text-sm border rounded px-2 py-1"
                                                            >
                                                                <option value="pending">{t('Pending')}</option>
                                                                <option value="confirmed">{t('Confirmed')}</option>
                                                                <option value="paid">{t('Paid')}</option>
                                                                <option value="canceled">{t('Canceled')}</option>
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Pagination */}
                                {pagination.pages > 1 && (
                                    <div className="px-4 py-3 border-t flex justify-between items-center">
                                        <p className="text-sm text-gray-500">
                                            {t('Page')} {pagination.page} {t('of')} {pagination.pages}
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => loadAffiliateOrders(selectedAffiliate!, pagination.page - 1)}
                                                disabled={pagination.page <= 1}
                                                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                                            >
                                                {t('Previous')}
                                            </button>
                                            <button
                                                onClick={() => loadAffiliateOrders(selectedAffiliate!, pagination.page + 1)}
                                                disabled={pagination.page >= pagination.pages}
                                                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                                            >
                                                {t('Next')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
